import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/auth/AuthContext";
import * as tus from "tus-js-client";

const MAX_FILES = 30;
const ALLOWED_TYPES = new Set([
  "image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif",
  "video/mp4", "video/quicktime", "video/x-msvideo", "video/webm",
]);
const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const MAX_VIDEO_BYTES = 49 * 1024 * 1024; // 49 MB — allineato al limite reale del bucket (50 MB)

// Soglia di routing: sotto → upload standard; sopra → TUS resumable.
// Per disabilitare TUS in emergenza: porta a Number.MAX_SAFE_INTEGER e rideploya.
const TUS_THRESHOLD_BYTES = 6 * 1024 * 1024; // 6 MB

const _projectRef = (process.env.REACT_APP_SUPABASE_URL || "").replace("https://", "").split(".")[0];
const TUS_ENDPOINT = `https://${_projectRef}.storage.supabase.co/storage/v1/upload/resumable`;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

// UUID v4 compatibile con iOS < 15.4 dove crypto.randomUUID() non esiste.
function safeUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const fmtDate = (s) => {
  if (!s) return null;
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return s; }
};

const makeItem = (file) => ({
  key: `${file.name}-${file.size}-${Date.now()}`,
  file,
  status: "idle",
  progress: 0,
  error: null,
});

// Upload resumable via TUS per file sopra TUS_THRESHOLD_BYTES.
// tusRef: React ref in cui viene salvata l'istanza tus.Upload attiva,
// usata dal gestore visibilitychange per riprendere dopo sospensione iOS.
async function uploadOneTus(weddingId, item, guestLabel, onProgress, dedication, tusRef) {
  const file = item.file;
  const ext = file.name.split(".").pop();
  const path = `${weddingId}/${safeUUID()}.${ext}`;
  const resourceType = file.type.startsWith("video/") ? "video" : "image";

  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: TUS_ENDPOINT,
      chunkSize: 6 * 1024 * 1024,
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      retryDelays: [0, 1000, 3000, 5000],
      headers: {
        authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      metadata: {
        bucketName: "wedding-media",
        objectName: path,
        contentType: file.type || "application/octet-stream",
        cacheControl: "3600",
      },
      onError: (err) => {
        if (tusRef) tusRef.current = null;
        reject(err);
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        onProgress?.(Math.round((bytesUploaded / bytesTotal) * 80));
      },
      onSuccess: () => {
        (async () => {
          try {
            onProgress?.(85);
            const { data: { publicUrl } } = supabase.storage.from("wedding-media").getPublicUrl(path);
            onProgress?.(90);
            const row = {
              wedding_id: weddingId,
              storage_path: path,
              public_url: publicUrl,
              resource_type: resourceType,
              guest_label: guestLabel || null,
              file_size: file.size,
              dedication: dedication || null,
            };
            // 2 tentativi di retry sul DB: se tutti falliscono il file resta orfano in Storage.
            let lastErr;
            for (let attempt = 0; attempt < 3; attempt++) {
              const { error: dbErr } = await supabase.from("uploads").insert(row);
              if (!dbErr) { lastErr = null; break; }
              lastErr = dbErr;
              if (attempt < 2) await new Promise(res => setTimeout(res, 1000 * (attempt + 1)));
            }
            if (lastErr) { reject(lastErr); return; }
            if (tusRef) tusRef.current = null;
            onProgress?.(100);
            resolve({ public_url: publicUrl, resource_type: resourceType });
          } catch (e) {
            reject(e);
          }
        })();
      },
    });
    if (tusRef) tusRef.current = upload;
    upload.start();
  });
}

async function uploadOne(weddingId, item, guestLabel, onProgress, dedication, tusRef) {
  const file = item.file;
  if (file.size > TUS_THRESHOLD_BYTES) {
    return uploadOneTus(weddingId, item, guestLabel, onProgress, dedication, tusRef);
  }
  const ext = file.name.split(".").pop();
  const path = `${weddingId}/${safeUUID()}.${ext}`;
  const resourceType = file.type.startsWith("video/") ? "video" : "image";

  const { error: storageErr } = await supabase.storage
    .from("wedding-media")
    .upload(path, file, { cacheControl: "3600", upsert: false });

  if (storageErr) throw storageErr;
  onProgress?.(80);

  const { data: { publicUrl } } = supabase.storage
    .from("wedding-media")
    .getPublicUrl(path);

  onProgress?.(90);

  const { error: dbErr } = await supabase.from("uploads").insert({
    wedding_id: weddingId,
    storage_path: path,
    public_url: publicUrl,
    resource_type: resourceType,
    guest_label: guestLabel || null,
    file_size: file.size,
    dedication: dedication || null,
  });

  if (dbErr) throw dbErr;
  onProgress?.(100);
  return { public_url: publicUrl, resource_type: resourceType };
}

export default function GuestWedding() {
  const { slug } = useParams();
  const [wedding, setWedding] = useState(null);
  const [loadingWedding, setLoadingWedding] = useState(true);
  const [guestLabel, setGuestLabel] = useState("");
  const [queue, setQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [diaryDedication, setDiaryDedication] = useState("");
  const [sendingDiary, setSendingDiary] = useState(false);
  const [weddingPlan, setWeddingPlan] = useState("free");
  const [photosUploaded, setPhotosUploaded] = useState(0);
  const fileInputRef = useRef(null);
  const processingRef = useRef(false);
  const queueRef = useRef([]);
  const tusUploadRef = useRef(null);

  useEffect(() => { queueRef.current = queue; }, [queue]);

  // Al ritorno in primo piano su iOS (tab precedentemente sospesa), riprende
  // l'upload TUS attivo dal punto in cui si era interrotto.
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      const upload = tusUploadRef.current;
      if (!upload) return;
      const previousUploads = await upload.findPreviousUploads();
      if (previousUploads.length > 0) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
      upload.start();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initWedding() {
      setLoadingWedding(true);
      try {
        const [{ data: weddingArr }, { data: info }] = await Promise.all([
          supabase.rpc("get_wedding_by_slug", { p_slug: slug }),
          supabase.rpc("get_wedding_info", { p_slug: slug }),
        ]);
        if (cancelled) return;
        const weddingData = weddingArr?.[0] ?? null;
        setWedding(weddingData);
        setWeddingPlan(info?.plan || "free");
        setPhotosUploaded(info?.photo_count || 0);
      } catch {
        if (!cancelled) setWedding(null);
      } finally {
        if (!cancelled) setLoadingWedding(false);
      }
    }

    initWedding();
    return () => { cancelled = true; };
  }, [slug]);

  const PHOTO_LIMITS = { free: 20, smart: 300, premium: Infinity };

  const enqueue = useCallback((files) => {
    const photoLimit = PHOTO_LIMITS[weddingPlan] ?? Infinity;
    const arr = Array.from(files).slice(0, MAX_FILES);
    const valid = [];

    const pendingQueuePhotos = queueRef.current.filter(
      (i) => !i.file.type.startsWith("video/") && i.status !== "error"
    ).length;
    let photosAddedThisBatch = 0;

    for (const f of arr) {
      if (!ALLOWED_TYPES.has(f.type)) { toast.error(`Tipo non supportato: ${f.name}`); continue; }

      const isVideo = f.type.startsWith("video/");

      if (isVideo && weddingPlan === "free") {
        toast.error("I video non sono disponibili nel piano gratuito");
        continue;
      }

      if (!isVideo && photoLimit !== Infinity) {
        const total = photosUploaded + pendingQueuePhotos + photosAddedThisBatch;
        if (total >= photoLimit) {
          toast.error(
            weddingPlan === "free"
              ? `Limite di ${photoLimit} foto raggiunto (piano gratuito)`
              : `Limite di ${photoLimit} foto raggiunto (piano Smart)`
          );
          break;
        }
        photosAddedThisBatch++;
      }

      const maxBytes = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      if (f.size > maxBytes) { toast.error(`File troppo grande: ${f.name}`); continue; }

      valid.push(makeItem(f));
    }
    if (valid.length) setQueue((q) => [...q, ...valid]);
  }, [weddingPlan, photosUploaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (processingRef.current) return;
    const idle = queue.find((i) => i.status === "idle");
    if (!idle || !wedding) return;
    processingRef.current = true;
    const updateItem = (key, patch) => setQueue((q) => q.map((i) => (i.key === key ? { ...i, ...patch } : i)));
    updateItem(idle.key, { status: "uploading", progress: 0 });
    uploadOne(wedding.id, idle, guestLabel, (p) => updateItem(idle.key, { progress: p }), null, tusUploadRef)
      .then(() => {
        updateItem(idle.key, { status: "done", progress: 100 });
      })
      .catch((e) => { const msg = e?.message || "Errore upload"; updateItem(idle.key, { status: "error", error: msg }); toast.error(`Errore: ${msg}`); })
      .finally(() => { processingRef.current = false; });
  }, [queue, wedding, guestLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = (key) => setQueue((q) => q.map((i) => (i.key === key ? { ...i, status: "idle", progress: 0, error: null } : i)));
  const remove = (key) => setQueue((q) => q.filter((i) => i.key !== key));

  const submitDiaryDedication = async () => {
    if (!diaryDedication.trim() || !wedding) return;
    setSendingDiary(true);
    try {
      const { error } = await supabase.from("uploads").insert({
        wedding_id: wedding.id,
        guest_label: guestLabel || null,
        dedication: diaryDedication.trim(),
        is_dedication_only: true,
        resource_type: "text",
      });
      if (error) throw error;
      setDiaryDedication("");
      toast.success("Dedica inviata!");
    } catch {
      toast.error("Errore nell'invio della dedica.");
    } finally {
      setSendingDiary(false);
    }
  };

  const onDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); enqueue(e.dataTransfer.files); }, [enqueue]);

  const doneCount = queue.filter((i) => i.status === "done").length;
  const pendingCount = queue.filter((i) => i.status === "waiting" || i.status === "idle" || i.status === "uploading").length;
  const errorCount = queue.filter((i) => i.status === "error").length;

  if (loadingWedding) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#c9a86a] animate-spin" />
      </div>
    );
  }

  if (!wedding) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-5xl mb-4">💍</p>
          <h1 className="text-2xl font-light text-neutral-800 mb-2">Pagina non trovata</h1>
          <p className="text-neutral-500 text-sm">Controlla il link ricevuto dagli sposi.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="w-full pt-14 pb-10 px-5 text-center bg-white">
        <p className="text-[10px] tracking-[0.3em] uppercase mb-5 font-medium flex items-center justify-center gap-2" style={{ color: "#c9a86a" }}>
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#c9a86a" }} />
          WEDFLOW · Galleria condivisa
        </p>
        <h1 className="font-display text-[38px] md:text-[68px] font-light tracking-[-0.02em] leading-[1.05] text-neutral-950">
          {wedding.title}
        </h1>
        {wedding.wedding_date && (
          <p className="mt-4 text-[12px] tracking-[0.18em] uppercase text-neutral-400">
            {fmtDate(wedding.wedding_date)}
            {wedding.location && ` · ${wedding.location}`}
          </p>
        )}
        {wedding.welcome_message && (
          <p className="mt-5 max-w-sm mx-auto text-[16px] leading-relaxed text-neutral-500 italic" style={{ fontFamily: "Georgia, serif" }}>
            "{wedding.welcome_message}"
          </p>
        )}
      </div>

      {/* Upload area */}
      <div className="max-w-lg mx-auto px-4 pb-16 space-y-4">

        {/* Nome ospite */}
        <div>
          <label className="block text-[12px] tracking-[0.1em] uppercase text-neutral-400 mb-2">Il tuo nome (opzionale)</label>
          <input
            type="text"
            value={guestLabel}
            onChange={(e) => setGuestLabel(e.target.value.slice(0, 60))}
            placeholder="Es. Zio Marco"
            className="w-full px-4 py-3.5 rounded-2xl border border-neutral-200 text-[15px] bg-white focus:outline-none focus:border-[#c9a86a] transition"
          />
        </div>

        {/* Drop zone — grande e touch-friendly */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => fileInputRef.current?.click()}
          className="relative rounded-3xl border-2 border-dashed transition-all cursor-pointer select-none"
          style={{
            borderColor: dragOver ? "#c9a86a" : "#e5e5e5",
            background: dragOver ? "#fdf8f0" : "#fafaf9",
            minHeight: "200px",
          }}
        >
          <div className="flex flex-col items-center justify-center py-14 px-6 pointer-events-none gap-3">
            {/* Camera icon */}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(201,168,106,0.1)" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c9a86a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </div>
            <div className="text-center">
              <p className="text-[15px] font-medium text-neutral-700">{dragOver ? "Rilascia qui" : "Tocca per aggiungere"}</p>
              <p className="mt-1 text-[12px] text-neutral-400">Foto e video dal tuo telefono</p>
              <p className="mt-0.5 text-[11px] text-neutral-300">JPG · PNG · HEIC · MP4 · MOV</p>
              <p className="mt-0.5 text-[10px] text-neutral-300">Max 49 MB per file</p>
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => enqueue(e.target.files)} />
        </div>

        {/* Stato upload */}
        {queue.length > 0 && (
          <div className="flex items-center justify-between text-[12px] px-1">
            <span>
              {doneCount > 0 && <span className="text-green-600 font-medium">{doneCount} caricati</span>}
              {pendingCount > 0 && <span className="text-neutral-400 ml-2">· {pendingCount} in coda</span>}
              {errorCount > 0 && <span className="text-red-500 ml-2">· {errorCount} errori</span>}
            </span>
            <button type="button" onClick={() => setQueue([])} className="text-neutral-400 hover:text-neutral-700">Cancella tutto</button>
          </div>
        )}

        {/* Lista file */}
        <div className="space-y-2">
          {queue.filter(i => i.status !== "waiting").map((item) => (
            <div key={item.key} className="flex items-start gap-3 p-3.5 rounded-2xl bg-white border border-neutral-100 shadow-sm">
              {/* Icona tipo */}
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: item.status === "done" ? "#f0fdf4" : item.status === "error" ? "#fef2f2" : "#f9f9f9" }}>
                {item.status === "done" && <span className="text-green-500 text-lg">✓</span>}
                {item.status === "error" && <span className="text-red-400 text-lg">!</span>}
                {item.status === "uploading" && <div className="w-4 h-4 rounded-full border-2 border-neutral-200 border-t-[#c9a86a] animate-spin" />}
                {(item.status === "idle" || item.status === "waiting") && <span className="text-neutral-300 text-sm">·</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-neutral-700 truncate font-medium">{item.file.name}</p>
                <p className="text-[11px] text-neutral-400">{(item.file.size / (1024 * 1024)).toFixed(1)} MB</p>
                {item.status === "uploading" && (
                  <div className="mt-2 h-1 rounded-full bg-neutral-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300" style={{ width: `${item.progress}%`, background: "#c9a86a" }} />
                  </div>
                )}
                {item.status === "error" && <p className="mt-1 text-[11px] text-red-500">{item.error}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0 pt-0.5">
                {item.status === "error" && (
                  <button onClick={() => retry(item.key)} className="p-1.5 text-neutral-400 hover:text-neutral-700 text-xs rounded-lg hover:bg-neutral-100">↺</button>
                )}
                {item.status !== "uploading" && item.status !== "waiting" && (
                  <button onClick={() => remove(item.key)} className="p-1.5 text-neutral-300 hover:text-neutral-600 text-xs rounded-lg hover:bg-neutral-100">✕</button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Successo finale */}
        {doneCount > 0 && pendingCount === 0 && errorCount === 0 && (
          <div className="text-center py-8 rounded-3xl border border-green-100" style={{ background: "#f0fdf4" }}>
            <p className="text-2xl mb-2">💚</p>
            <p className="font-medium text-green-700 text-[15px]">
              {doneCount === 1 ? "Grazie! Il tuo file è stato caricato." : `Grazie! ${doneCount} file caricati.`}
            </p>
            <p className="text-[13px] text-green-500 mt-1">Gli sposi potranno vederli nella loro galleria.</p>
            <button
              type="button"
              onClick={() => setQueue([])}
              className="mt-5 px-6 py-2.5 rounded-full text-[13px] font-medium text-green-700 border border-green-200 hover:bg-green-50 transition"
            >
              Carica altri
            </button>
          </div>
        )}

        {/* Sezione solo-dedica — solo Premium */}
        {weddingPlan === "premium" && <div className="mt-10 pt-8 border-t border-neutral-100">
          <h2 className="font-display text-[22px] font-light tracking-[-0.01em] text-neutral-950 mb-1">
            Scrivi una dedica per gli sposi
          </h2>
          <p className="text-[13px] text-neutral-400 mb-4">
            Il tuo messaggio sarà nel loro Diario dei Ricordi
          </p>
          <textarea
            value={diaryDedication}
            onChange={(e) => setDiaryDedication(e.target.value)}
            maxLength={1000}
            rows={4}
            placeholder="Racconta un ricordo, un augurio, qualcosa di speciale..."
            className="w-full bg-neutral-50 border border-neutral-200 rounded-2xl px-4 py-3 text-[13px] text-neutral-700 outline-none resize-none focus:border-[#c9a86a] transition-colors"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-neutral-400">{diaryDedication.length}/1000</span>
            <button
              type="button"
              onClick={submitDiaryDedication}
              disabled={sendingDiary || !diaryDedication.trim()}
              className="px-5 py-2.5 rounded-full text-[12px] tracking-wide font-medium text-white disabled:opacity-40 transition-all"
              style={{ background: "#c9a86a" }}
            >
              {sendingDiary ? "Invio…" : "Invia al Diario dei Ricordi"}
            </button>
          </div>
        </div>}
      </div>

      <div className="text-center py-8 text-[10px] tracking-[0.2em] uppercase text-neutral-300">
        Powered by WEDFLOW
      </div>

    </div>
  );
}
